'use client'
import React from 'react'
import {
    Filter,
    UpdateFilterValueModel,
    FilterId,
    FilterOperator,
    FilterProviderModel,
    FilterValue,
} from '../types/Filters'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

export interface FilterProviderProps {
    initialFilters?: Filter[]
    onFiltersUpdate?: (currentActiveFilters: Filter[]) => void
    children: ReactNode
}

export const FilterContext = createContext<FilterProviderModel>({
    isInitialized: false,
    filters: [],
    hasFiltersWithValue: false,
    hasClearableFilters: false,
    updateFilterValue: () => undefined,
    isExpanded: false,
    getFilterId: () => '',
    getFilterValue: () => undefined,
    hasFilterForKey: () => false,
    toggleFilters: () => undefined,
    clearFilters: () => undefined,
    registerFilter: () => undefined,
    getFilterById: () => undefined,
})

/** @deprecated use useContext(FilterContext) */
export const useFilterContext = (): FilterProviderModel => {
    return useContext(FilterContext)
}

export const FilterProvider = ({ onFiltersUpdate, children, initialFilters }: FilterProviderProps) => {
    const [filters, setFilters] = useState<Filter[]>(initialFilters || [])
    const [hasClearableFilters, setHasClearableFilters] = useState<boolean>(false)
    const [hasFiltersWithValue, setHasFiltersWithValue] = useState<boolean>(false)
    const [isExpanded, setIsExpanded] = useState<boolean>(false)

    const updateFilterValue: UpdateFilterValueModel = ({ filter, newValue }): void => {
        const matchingFilter = filters.find((stateFilter) => stateFilter.id === filter.id)
        if (!matchingFilter) return
        if (matchingFilter.value === newValue) return

        setFilters((stateFilters) => {
            return [
                ...stateFilters.filter((stateFilter) => stateFilter.id !== matchingFilter.id),
                { ...matchingFilter, value: newValue },
            ]
        })
    }

    const toggleFilters = (): void => {
        setIsExpanded((expanded) => !expanded)
    }

    const clearFilters = (): void => {
        setFilters((stateFilters) => [
            ...stateFilters.map((filter) => (filter.nonClearable ? filter : { ...filter, value: undefined })),
        ])
    }

    const getFilterId = (key: string, operator: FilterOperator): string => {
        return `${key}__${operator}`
    }

    const getFilterById = (id: FilterId): Filter | undefined => {
        return filters.find((filter) => filter.id === id)
    }

    const getFilterValue = (id: FilterId): FilterValue | undefined => {
        return filters.find((filter) => filter.id === id)?.value
    }

    const hasFilterForKey = (key: string): boolean => {
        return filters.some((filter) => filter.key === key && !!filter.value)
    }

    const registerFilter = (filter: Filter) => {
        setFilters((stateFilters) => {
            if (stateFilters.find((stateFilter) => stateFilter.id === filter.id)) {
                return stateFilters
            }
            return [...stateFilters, filter]
        })
    }

    useEffect(() => {
        setHasClearableFilters(filters.filter((filter) => !filter.nonClearable && !!filter.value).length > 0)
        setHasFiltersWithValue(filters.filter((filter) => !!filter.value).length > 0)
        if (onFiltersUpdate) {
            onFiltersUpdate(filters.filter((filter) => !!filter.value))
        }
    }, [filters])

    const FilterProviderData: FilterProviderModel = {
        isInitialized: true,
        filters,
        hasFiltersWithValue,
        hasClearableFilters,
        updateFilterValue,
        isExpanded,
        getFilterId,
        getFilterValue,
        hasFilterForKey,
        toggleFilters,
        clearFilters,
        registerFilter,
        getFilterById,
    }

    return <FilterContext.Provider value={FilterProviderData}>{children}</FilterContext.Provider>
}
